import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import BarcodeScannerIcon from '@mui/icons-material/ViewWeek';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ProductsSearch from './ProductsSearch';
import ProductsResults from './ProductsResults';
import ProductDetail from '../../redux/containers/products/ProductDetail';
import ExcelUpload from '../../redux/containers/products/ExcelUpload';
import ScannerModal from '../../redux/containers/scanner/ScannerModal';
import { COLORS } from '../tasks/constants';

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'hidden',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 2,
  },
  searchHeader: {
    px: { xs: 3, md: 5 },
    pt: 2,
    pb: 1.5,
  },
  titleBlock: {
    mb: 1,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  storeName: {
    display: { xs: 'none', sm: 'block' },
    fontSize: '0.7rem',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 260,
    pt: 0.5,
    pl: 2,
  },
  title: {
    fontSize: 24,
    fontWeight: 800,
    color: 'text.primary',
    mb: 0.25,
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: 12,
    color: 'text.secondary',
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1.5,
    flexWrap: { xs: 'wrap', sm: 'nowrap' },
    mt: 1,
  },
  searchWrapper: {
    flex: 1,
    minWidth: 0,
    maxWidth: { xs: '100%', sm: 560 },
    width: { xs: '100%', sm: 'auto' },
  },
  scanBtn: {
    bgcolor: COLORS.SCANNER,
    color: '#fff',
    px: 2,
    borderRadius: 1,
    fontWeight: 600,
    fontSize: 13,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '&:hover': { bgcolor: COLORS.SCANNER_HOVER },
    '&:active': { transform: 'scale(0.96)' },
  },
  excelBtn: {
    bgcolor: COLORS.EXCEL,
    color: '#fff',
    px: 2,
    borderRadius: 1,
    fontWeight: 600,
    fontSize: 13,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '&:hover': { bgcolor: COLORS.EXCEL_HOVER },
    '&:active': { transform: 'scale(0.96)' },
  },
  buttonsWrapper: {
    display: 'flex',
    gap: 0.75,
    flexShrink: 0,
    width: { xs: '100%', sm: 'auto' },
  },
  progressSpinner: {
    color: 'text.disabled',
    flexShrink: 0,
  },
};

export default function ProductsView({
  searchResults,
  searchResultsFilter,
  loadingSearch,
  currentStore,
  currentStoreName,
  getLayout,
  currentChain,
  searchProduct,
  setSearchFilter,
  resetSearchFilter,
  resetSearch,
  restoreSearch,
  reportOptions,
  reportOptionsLoading,
  reportOptionsFetchedStore,
  fetchReportOptions,
}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [excelOpen, setExcelOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const searchRef = useRef(null);
  // Snapshot of the results visible behind the scanner modal, so the Generate task
  // flow (which dispatches searchProduct on each scan) doesn't overwrite what the
  // user was looking at. Captured when the modal opens, used while it's open.
  const scannerSnapshotRef = useRef(null);
  // Reset state on store change, unless arriving from scanner with results in the URL
  useEffect(() => {
    const comingFromScanner = searchParams.get('barcode') || searchParams.get('search');
    if (comingFromScanner) {
      setHasSearched(true);
      setSearchInput('');
      setSearchParams({}, { replace: true });
      return;
    }
    setHasSearched(false);
    setSelectedProduct(null);
    setSearchInput('');
    resetSearchFilter();
    resetSearch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // intentionally runs only on store change — searchParams, resetSearch, resetSearchFilter are stable
  }, [currentStore]);

  // Handle scanner navigation when already on /products (currentStore didn't change)
  useEffect(() => {
    const comingFromScanner = searchParams.get('barcode') || searchParams.get('search');
    if (!comingFromScanner) return;
    setHasSearched(true);
    setSearchInput('');
    setSearchParams({}, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // intentionally runs only on searchParams change — setSearchParams setter is stable
  }, [searchParams]);


  useEffect(() => {
    if (currentStore) getLayout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // intentionally runs only on store change — getLayout is a stable Redux dispatch
  }, [currentStore]);

  useEffect(() => {
    if (currentStore && reportOptionsFetchedStore !== currentStore) {
      fetchReportOptions(currentStore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // intentionally runs only on store change — fetchReportOptions is a stable Redux dispatch
  }, [currentStore]);

  const buttonsReady = !reportOptionsLoading && reportOptionsFetchedStore === currentStore;
  const showExcelBtn = buttonsReady && Array.isArray(reportOptions) &&
    reportOptions.some(option => option.excel_reporting === true);
  const showScanBtn = buttonsReady;

  const handleSearch = useCallback(
    (key, value) => {
      setHasSearched(true);
      searchProduct(key, value, { silent: true });
    },
    [searchProduct],
  );

  const handleReset = useCallback(() => {
    resetSearch();
  }, [resetSearch]);

  const handleClear = useCallback(() => {
    setHasSearched(false);
    setSearchInput('');
    resetSearch();
  }, [resetSearch]);

  const openScanner = useCallback(() => {
    searchRef.current?.cancel();
    scannerSnapshotRef.current = {
      results: searchResults,
      resultsFilter: searchResultsFilter,
      hasSearched,
      input: searchInput,
    };
    setScannerOpen(true);
  }, [searchResults, searchResultsFilter, hasSearched, searchInput]);

  // While the scanner is open, render the snapshot instead of the live Redux state —
  // a scan in Generate task mode dispatches searchProduct which would otherwise
  // overwrite the results visible behind the modal.
  const snapshot = scannerOpen ? scannerSnapshotRef.current : null;
  const displayedResults = snapshot ? snapshot.results : searchResults;
  const displayedResultsFilter = snapshot ? snapshot.resultsFilter : searchResultsFilter;
  const displayedHasSearched = snapshot ? snapshot.hasSearched : hasSearched;
  const displayedSearchInput = snapshot ? snapshot.input : searchInput;

  return (
    <>
    <Box sx={styles.root}>
      <Box sx={styles.searchHeader}>
        <Box sx={styles.titleBlock}>
          <Box sx={styles.titleRow}>
            <Typography variant="h6" sx={styles.title}>
              {t('bruno.products.product_title')}
            </Typography>
            {currentStoreName && (
              <Typography sx={styles.storeName}>
                {currentChain ? `${currentChain} · ` : ''}
                {currentStoreName.split(' - ').slice(1).join(' - ') || currentStoreName}
              </Typography>
            )}
          </Box>
          <Typography variant="body2" sx={styles.subtitle}>
            {t('bruno.products.search_subtitle', 'Search by name, internal ID or barcode')}
          </Typography>
        </Box>

        <Box sx={styles.searchRow}>
          <Box sx={styles.searchWrapper}>
            <ProductsSearch
              ref={searchRef}
              searchProduct={handleSearch}
              resetSearch={handleReset}
              onClear={handleClear}
              options={displayedResults}
              value={displayedSearchInput}
              onValueChange={setSearchInput}
            />
          </Box>
          {!buttonsReady && currentStore ? (
            <CircularProgress size={28} thickness={3} sx={styles.progressSpinner} />
          ) : (showExcelBtn || showScanBtn) && (
            <Box sx={styles.buttonsWrapper}>
              {showExcelBtn && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ImportExportIcon />}
                  fullWidth={!showScanBtn}
                  sx={{ ...styles.excelBtn, flex: { xs: 1, sm: 'none' } }}
                  onClick={() => setExcelOpen(true)}
                >
                  {t('bruno.excel.title', 'Excel')}
                </Button>
              )}
              {showScanBtn && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<BarcodeScannerIcon />}
                  fullWidth={!showExcelBtn}
                  sx={{ ...styles.scanBtn, flex: { xs: 1, sm: 'none' } }}
                  onClick={openScanner}
                >
                  {t('bruno.products.scan', 'Scan')}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <ProductsResults
        searchResults={displayedResults}
        searchResultsFilter={displayedResultsFilter}
        loadingSearch={loadingSearch && !scannerOpen}
        hasSearched={displayedHasSearched}
        setSearchFilter={setSearchFilter}
        resetSearchFilter={resetSearchFilter}
        onProductSelect={setSelectedProduct}
      />
      <ProductDetail
        open={selectedProduct !== null}
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />
      <ExcelUpload
        open={excelOpen}
        onClose={() => setExcelOpen(false)}
      />
      <ScannerModal
        open={scannerOpen}
        onClose={({ wipedSearch, keepResults } = {}) => {
          const snap = scannerSnapshotRef.current;
          setScannerOpen(false);
          scannerSnapshotRef.current = null;
          // Search-tab scan landed results in Redux and is navigating with params.
          // Don't restore the snapshot — it would overwrite the fresh scan results.
          if (keepResults) return;
          // If a prior ProductsView search was captured on open, always restore it
          // on close — the scanner dispatches resetSearch() unconditionally on
          // open, so Redux is empty even when the user closes without scanning.
          if (snap?.hasSearched && snap.results?.length) {
            restoreSearch(snap.results);
            setHasSearched(true);
            setSearchInput(snap.input || '');
          } else if (wipedSearch) {
            setHasSearched(false);
            setSearchInput('');
          }
        }}
      />
    </Box>
    </>
  );
}
